import {
  Add as AddIcon,
  Code as CodeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  PhoneIphone as PhoneIcon,
  PlayArrow as PlayIcon,
  Visibility as PreviewIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createAlertMilestone,
  createAlertSchedule,
  createAlertTemplate,
  deleteAlertMilestone,
  deleteAlertSchedule,
  deleteAlertTemplate,
  getAlertMilestones,
  getAlertSchedules,
  getAlertTemplates,
  previewAlertTemplate,
  triggerAlertSchedule,
  updateAlertMilestone,
  updateAlertSchedule,
  updateAlertTemplate,
} from "../api/alerts";

const TEMPLATE_VARIABLES = [
  "{{ user_name }}",
  "{{ project_name }}",
  "{{ total_revenue }}",
  "{{ total_impressions }}",
  "{{ sales_count }}",
  "{{ total_royalty_revenue }}",
  "{{ total_impression_revenue }}",
  "{{ period_start }}",
  "{{ period_end }}",
  "{{ milestone_type }}",
  "{{ milestone_value }}",
  "{{ asset_title }}",
];

const CATEGORIES = [
  "weekly_summary",
  "monthly_breakdown",
  "quarterly_review",
  "milestone_reached",
  "custom",
];

const FREQUENCIES = ["weekly", "monthly", "quarterly"];
const CHANNELS = ["email", "sms", "both"];
const METRICS = [
  "total_sales_count",
  "total_royalty_revenue",
  "total_impressions",
  "total_impression_revenue",
];

export default function AlertSettings() {
  const { t } = useTranslation("alerts");
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    category: "custom",
    subject_template: "",
    body_template: "",
    sms_template: "",
  });
  const [editorView, setEditorView] = useState("edit"); // "edit" | "preview"
  const [editorPreviewTab, setEditorPreviewTab] = useState("email"); // "email" | "sms"

  // Schedules state
  const [schedules, setSchedules] = useState([]);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    template: "",
    frequency: "weekly",
    channel: "email",
    is_active: true,
  });

  // Milestones state
  const [milestones, setMilestones] = useState([]);
  const [milestoneDialog, setMilestoneDialog] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneForm, setMilestoneForm] = useState({
    template: "",
    metric: "total_sales_count",
    threshold_value: "",
    channel: "email",
    is_active: true,
    asset: "",
  });

  useEffect(() => {
    loadTemplates();
    loadSchedules();
    loadMilestones();
  }, []);

  const loadTemplates = async () => {
    try {
      setTemplates(await getAlertTemplates());
    } catch {
      /* ignore */
    }
  };
  const loadSchedules = async () => {
    try {
      setSchedules(await getAlertSchedules());
    } catch {
      /* ignore */
    }
  };
  const loadMilestones = async () => {
    try {
      setMilestones(await getAlertMilestones());
    } catch {
      /* ignore */
    }
  };

  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // --- Template Handlers ---
  const openCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: "",
      category: "custom",
      subject_template: "",
      body_template: "",
      sms_template: "",
    });
    setTemplateDialog(true);
  };
  const openEditTemplate = (tpl) => {
    setEditingTemplate(tpl);
    setTemplateForm({
      name: tpl.name,
      category: tpl.category,
      subject_template: tpl.subject_template,
      body_template: tpl.body_template,
      sms_template: tpl.sms_template || "",
    });
    setTemplateDialog(true);
  };
  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate) {
        await updateAlertTemplate(editingTemplate.id, templateForm);
      } else {
        await createAlertTemplate(templateForm);
      }
      setTemplateDialog(false);
      loadTemplates();
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };
  const handleDeleteTemplate = async (id) => {
    if (!window.confirm(t("templates.confirmDelete"))) return;
    try {
      await deleteAlertTemplate(id);
      loadTemplates();
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };
  const handlePreviewTemplate = async (id) => {
    try {
      setPreviewLoading(true);
      const data = await previewAlertTemplate(id);
      setPreviewData(data);
      setPreviewDialog(true);
    } catch (e) {
      showSnack(e.message, "error");
    } finally {
      setPreviewLoading(false);
    }
  };
  const handleInlinePreview = async (id) => {
    try {
      setPreviewLoading(true);
      const data = await previewAlertTemplate(id);
      setPreviewData(data);
      setEditorView("preview");
    } catch (e) {
      showSnack(e.message, "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  // --- Schedule Handlers ---
  const openCreateSchedule = () => {
    setEditingSchedule(null);
    setScheduleForm({
      template: "",
      frequency: "weekly",
      channel: "email",
      is_active: true,
    });
    setScheduleDialog(true);
  };
  const openEditSchedule = (s) => {
    setEditingSchedule(s);
    setScheduleForm({
      template: s.template,
      frequency: s.frequency,
      channel: s.channel,
      is_active: s.is_active,
    });
    setScheduleDialog(true);
  };
  const handleSaveSchedule = async () => {
    try {
      if (editingSchedule) {
        await updateAlertSchedule(editingSchedule.id, scheduleForm);
      } else {
        await createAlertSchedule(scheduleForm);
      }
      setScheduleDialog(false);
      loadSchedules();
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };
  const handleDeleteSchedule = async (id) => {
    if (!window.confirm(t("schedules.confirmDelete"))) return;
    try {
      await deleteAlertSchedule(id);
      loadSchedules();
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };
  const handleTriggerSchedule = async (id) => {
    try {
      await triggerAlertSchedule(id);
      showSnack(t("schedules.triggered"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  // --- Milestone Handlers ---
  const openCreateMilestone = () => {
    setEditingMilestone(null);
    setMilestoneForm({
      template: "",
      metric: "total_sales_count",
      threshold_value: "",
      channel: "email",
      is_active: true,
      asset: "",
    });
    setMilestoneDialog(true);
  };
  const openEditMilestone = (m) => {
    setEditingMilestone(m);
    setMilestoneForm({
      template: m.template,
      metric: m.metric,
      threshold_value: m.threshold_value,
      channel: m.channel,
      is_active: m.is_active,
      asset: m.asset || "",
    });
    setMilestoneDialog(true);
  };
  const handleSaveMilestone = async () => {
    try {
      const data = { ...milestoneForm, asset: milestoneForm.asset || null };
      if (editingMilestone) {
        await updateAlertMilestone(editingMilestone.id, data);
      } else {
        await createAlertMilestone(data);
      }
      setMilestoneDialog(false);
      loadMilestones();
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };
  const handleDeleteMilestone = async (id) => {
    if (!window.confirm(t("milestones.confirmDelete"))) return;
    try {
      await deleteAlertMilestone(id);
      loadMilestones();
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t("page.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("page.description")}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={t("tabs.templates")} />
        <Tab label={t("tabs.schedules")} />
        <Tab label={t("tabs.milestones")} />
      </Tabs>

      {/* Templates Tab */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">{t("templates.title")}</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateTemplate}
            >
              {t("templates.create")}
            </Button>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("templates.name")}</TableCell>
                <TableCell>{t("templates.category")}</TableCell>
                <TableCell>{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((tpl) => (
                <TableRow key={tpl.id}>
                  <TableCell>
                    {tpl.name}
                    {tpl.is_preset && (
                      <Chip
                        label={t("templates.preset")}
                        size="small"
                        sx={{ ml: 1 }}
                        color="primary"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {t(`templates.categories.${tpl.category}`)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t("templates.preview")}>
                      <IconButton
                        size="small"
                        onClick={() => handlePreviewTemplate(tpl.id)}
                      >
                        <PreviewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("templates.edit")}>
                      <IconButton
                        size="small"
                        onClick={() => openEditTemplate(tpl)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {!tpl.is_preset && (
                      <Tooltip title={t("templates.delete")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTemplate(tpl.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Schedules Tab */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">{t("schedules.title")}</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateSchedule}
            >
              {t("schedules.create")}
            </Button>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("schedules.template")}</TableCell>
                <TableCell>{t("schedules.frequency")}</TableCell>
                <TableCell>{t("schedules.channel")}</TableCell>
                <TableCell>{t("schedules.active")}</TableCell>
                <TableCell>{t("schedules.nextRun")}</TableCell>
                <TableCell>{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.template_name}</TableCell>
                  <TableCell>
                    {t(`schedules.frequencies.${s.frequency}`)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`schedules.channels.${s.channel}`)}
                      size="small"
                      color={
                        s.channel === "email"
                          ? "primary"
                          : s.channel === "sms"
                            ? "success"
                            : "secondary"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={s.is_active ? t("schedules.active") : "Inactive"}
                      size="small"
                      color={s.is_active ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {s.next_run_at
                      ? new Date(s.next_run_at).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t("schedules.triggerNow")}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleTriggerSchedule(s.id)}
                      >
                        <PlayIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("schedules.edit")}>
                      <IconButton
                        size="small"
                        onClick={() => openEditSchedule(s)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("templates.delete")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteSchedule(s.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Milestones Tab */}
      {tab === 2 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">{t("milestones.title")}</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateMilestone}
            >
              {t("milestones.create")}
            </Button>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("milestones.metric")}</TableCell>
                <TableCell>{t("milestones.threshold")}</TableCell>
                <TableCell>{t("schedules.channel")}</TableCell>
                <TableCell>{t("milestones.asset")}</TableCell>
                <TableCell>{t("schedules.active")}</TableCell>
                <TableCell>{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {milestones.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{t(`milestones.metrics.${m.metric}`)}</TableCell>
                  <TableCell>{m.threshold_value}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(`schedules.channels.${m.channel}`)}
                      size="small"
                      color={
                        m.channel === "email"
                          ? "primary"
                          : m.channel === "sms"
                            ? "success"
                            : "secondary"
                      }
                    />
                  </TableCell>
                  <TableCell>{m.asset_title || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={m.is_active ? t("schedules.active") : "Inactive"}
                      size="small"
                      color={m.is_active ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t("milestones.edit")}>
                      <IconButton
                        size="small"
                        onClick={() => openEditMilestone(m)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("templates.delete")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteMilestone(m.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Template Create/Edit Dialog — split edit/preview */}
      <Dialog
        open={templateDialog}
        onClose={() => {
          setTemplateDialog(false);
          setEditorView("edit");
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: "80vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6">
            {editingTemplate ? t("templates.edit") : t("templates.create")}
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={editorView}
            exclusive
            onChange={(_, v) => {
              if (v === "preview" && editingTemplate)
                handleInlinePreview(editingTemplate.id);
              else if (v) setEditorView(v);
            }}
          >
            <ToggleButton value="edit">
              <CodeIcon sx={{ mr: 0.5, fontSize: 18 }} /> Edit
            </ToggleButton>
            <ToggleButton value="preview" disabled={!editingTemplate}>
              <PreviewIcon sx={{ mr: 0.5, fontSize: 18 }} /> Preview
            </ToggleButton>
          </ToggleButtonGroup>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {editorView === "edit" ? (
            <Box sx={{ p: 3 }}>
              {/* Row 1: Name + Category */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label={t("templates.name")}
                  value={templateForm.name}
                  onChange={(e) =>
                    setTemplateForm({ ...templateForm, name: e.target.value })
                  }
                />
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>{t("templates.category")}</InputLabel>
                  <Select
                    value={templateForm.category}
                    label={t("templates.category")}
                    onChange={(e) =>
                      setTemplateForm({
                        ...templateForm,
                        category: e.target.value,
                      })
                    }
                  >
                    {CATEGORIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {t(`templates.categories.${c}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Subject */}
              <TextField
                fullWidth
                label={t("templates.subject")}
                value={templateForm.subject_template}
                onChange={(e) =>
                  setTemplateForm({
                    ...templateForm,
                    subject_template: e.target.value,
                  })
                }
                helperText="The email subject line. Use variables like {{ project_name }} to personalize."
                sx={{ mb: 2 }}
              />

              {/* Email Body */}
              <Typography
                variant="subtitle2"
                sx={{ mb: 0.5, color: "text.secondary" }}
              >
                Email Body (HTML)
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 1, color: "text.disabled" }}
              >
                The full HTML content of the email. Preset templates come
                pre-designed — edit with care or use Preview to see the result.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={templateForm.body_template}
                onChange={(e) =>
                  setTemplateForm({
                    ...templateForm,
                    body_template: e.target.value,
                  })
                }
                sx={{
                  mb: 3,
                  "& .MuiInputBase-root": {
                    fontFamily: "monospace",
                    fontSize: 12,
                  },
                }}
              />

              <Divider sx={{ mb: 3 }} />

              {/* SMS — friendlier layout */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  mb: 2,
                }}
              >
                <PhoneIcon sx={{ color: "success.main", mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    SMS Message
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 1, color: "text.disabled" }}
                  >
                    Plain text sent via SMS. Keep it short and end with a link
                    so recipients can view full details.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={templateForm.sms_template}
                    onChange={(e) =>
                      setTemplateForm({
                        ...templateForm,
                        sms_template: e.target.value,
                      })
                    }
                    inputProps={{ maxLength: 1600 }}
                    sx={{ "& .MuiInputBase-root": { fontSize: 14 } }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.disabled">
                      Variables like {"{{ project_name }}"} will be replaced
                      with real values
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          templateForm.sms_template.length > 160
                            ? "warning.main"
                            : "text.disabled",
                      }}
                    >
                      {templateForm.sms_template.length}/160 chars
                      {templateForm.sms_template.length > 160 &&
                        " (multi-segment)"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Variables — with descriptions */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("templates.variables")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 1.5, color: "text.disabled" }}
              >
                Click any variable to copy it, then paste into the subject,
                body, or SMS field.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {TEMPLATE_VARIABLES.map((v) => (
                  <Chip
                    key={v}
                    label={v.replace(/\{\{ | \}\}/g, "")}
                    size="small"
                    variant="outlined"
                    clickable
                    sx={{ fontFamily: "monospace", fontSize: 12 }}
                    onClick={() => {
                      navigator.clipboard.writeText(v);
                      showSnack(`Copied: ${v}`);
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            /* PREVIEW MODE */
            <Box sx={{ p: 3 }}>
              {previewLoading ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2, color: "text.secondary" }}>
                    Generating preview...
                  </Typography>
                </Box>
              ) : previewData ? (
                <>
                  <ToggleButtonGroup
                    size="small"
                    value={editorPreviewTab}
                    exclusive
                    onChange={(_, v) => v && setEditorPreviewTab(v)}
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="email">
                      <EmailIcon sx={{ mr: 0.5, fontSize: 18 }} /> Email
                    </ToggleButton>
                    <ToggleButton value="sms">
                      <PhoneIcon sx={{ mr: 0.5, fontSize: 18 }} /> SMS
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {editorPreviewTab === "email" && (
                    <Box>
                      {/* Subject preview */}
                      <Box
                        sx={{
                          bgcolor: "grey.50",
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          mb: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          Subject
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          {previewData.subject}
                        </Typography>
                      </Box>
                      {/* Rendered HTML */}
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          overflow: "hidden",
                          bgcolor: "#fff",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                        }}
                      >
                        <Box
                          sx={{ p: 0 }}
                          dangerouslySetInnerHTML={{ __html: previewData.body }}
                        />
                      </Box>
                    </Box>
                  )}

                  {editorPreviewTab === "sms" && previewData.sms_body && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 2 }}
                    >
                      {/* Phone mockup */}
                      <Box
                        sx={{
                          width: 320,
                          minHeight: 400,
                          border: "3px solid #333",
                          borderRadius: "28px",
                          bgcolor: "#f5f5f5",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Phone top bar */}
                        <Box
                          sx={{
                            bgcolor: "#333",
                            color: "#fff",
                            textAlign: "center",
                            py: 1,
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          RoyaltyX
                        </Box>
                        {/* Message bubble */}
                        <Box
                          sx={{
                            p: 2,
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: "#007bff",
                              color: "#fff",
                              p: 1.5,
                              borderRadius: "16px 16px 4px 16px",
                              fontSize: 14,
                              lineHeight: 1.5,
                              maxWidth: "90%",
                              alignSelf: "flex-end",
                              wordBreak: "break-word",
                            }}
                          >
                            {previewData.sms_body}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              textAlign: "right",
                              mt: 0.5,
                              color: "text.disabled",
                            }}
                          >
                            {previewData.sms_body.length} chars
                            {previewData.sms_body.length > 160
                              ? ` (${Math.ceil(previewData.sms_body.length / 153)} segments)`
                              : " (1 segment)"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Typography
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 6 }}
                >
                  Save the template first, then switch to Preview to see how it
                  looks with sample data.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setTemplateDialog(false);
              setEditorView("edit");
            }}
          >
            {t("common.cancel")}
          </Button>
          {editorView === "edit" && (
            <Button variant="contained" onClick={handleSaveTemplate}>
              {t("common.save")}
            </Button>
          )}
          {editorView === "preview" && (
            <Button variant="outlined" onClick={() => setEditorView("edit")}>
              Back to Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Standalone Preview Dialog (from table eye icon) */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">{t("templates.preview")}</Typography>
          {previewData && (
            <ToggleButtonGroup
              size="small"
              value={editorPreviewTab}
              exclusive
              onChange={(_, v) => v && setEditorPreviewTab(v)}
            >
              <ToggleButton value="email">
                <EmailIcon sx={{ mr: 0.5, fontSize: 18 }} /> Email
              </ToggleButton>
              <ToggleButton value="sms">
                <PhoneIcon sx={{ mr: 0.5, fontSize: 18 }} /> SMS
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </DialogTitle>
        <DialogContent>
          {previewLoading && <LinearProgress sx={{ mb: 2 }} />}
          {previewData && editorPreviewTab === "email" && (
            <>
              <Box
                sx={{
                  bgcolor: "grey.50",
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  mb: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" color="text.disabled">
                  Subject
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {previewData.subject}
                </Typography>
              </Box>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#fff",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                }}
              >
                <Box dangerouslySetInnerHTML={{ __html: previewData.body }} />
              </Box>
            </>
          )}
          {previewData &&
            editorPreviewTab === "sms" &&
            previewData.sms_body && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Box
                  sx={{
                    width: 320,
                    minHeight: 360,
                    border: "3px solid #333",
                    borderRadius: "28px",
                    bgcolor: "#f5f5f5",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#333",
                      color: "#fff",
                      textAlign: "center",
                      py: 1,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    RoyaltyX
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: "#007bff",
                        color: "#fff",
                        p: 1.5,
                        borderRadius: "16px 16px 4px 16px",
                        fontSize: 14,
                        lineHeight: 1.5,
                        maxWidth: "90%",
                        alignSelf: "flex-end",
                        wordBreak: "break-word",
                      }}
                    >
                      {previewData.sms_body}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "right",
                        mt: 0.5,
                        color: "text.disabled",
                      }}
                    >
                      {previewData.sms_body.length} chars
                      {previewData.sms_body.length > 160
                        ? ` (${Math.ceil(previewData.sms_body.length / 153)} segments)`
                        : " (1 segment)"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Create/Edit Dialog */}
      <Dialog
        open={scheduleDialog}
        onClose={() => setScheduleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSchedule ? t("schedules.edit") : t("schedules.create")}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>{t("schedules.template")}</InputLabel>
            <Select
              value={scheduleForm.template}
              label={t("schedules.template")}
              onChange={(e) =>
                setScheduleForm({ ...scheduleForm, template: e.target.value })
              }
            >
              {templates.map((tpl) => (
                <MenuItem key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("schedules.frequency")}</InputLabel>
            <Select
              value={scheduleForm.frequency}
              label={t("schedules.frequency")}
              onChange={(e) =>
                setScheduleForm({ ...scheduleForm, frequency: e.target.value })
              }
            >
              {FREQUENCIES.map((f) => (
                <MenuItem key={f} value={f}>
                  {t(`schedules.frequencies.${f}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("schedules.channel")}</InputLabel>
            <Select
              value={scheduleForm.channel}
              label={t("schedules.channel")}
              onChange={(e) =>
                setScheduleForm({ ...scheduleForm, channel: e.target.value })
              }
            >
              {CHANNELS.map((c) => (
                <MenuItem key={c} value={c}>
                  {t(`schedules.channels.${c}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography>{t("schedules.active")}</Typography>
            <Switch
              checked={scheduleForm.is_active}
              onChange={(e) =>
                setScheduleForm({
                  ...scheduleForm,
                  is_active: e.target.checked,
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSaveSchedule}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Milestone Create/Edit Dialog */}
      <Dialog
        open={milestoneDialog}
        onClose={() => setMilestoneDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMilestone ? t("milestones.edit") : t("milestones.create")}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>{t("schedules.template")}</InputLabel>
            <Select
              value={milestoneForm.template}
              label={t("schedules.template")}
              onChange={(e) =>
                setMilestoneForm({ ...milestoneForm, template: e.target.value })
              }
            >
              {templates.map((tpl) => (
                <MenuItem key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("milestones.metric")}</InputLabel>
            <Select
              value={milestoneForm.metric}
              label={t("milestones.metric")}
              onChange={(e) =>
                setMilestoneForm({ ...milestoneForm, metric: e.target.value })
              }
            >
              {METRICS.map((m) => (
                <MenuItem key={m} value={m}>
                  {t(`milestones.metrics.${m}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            label={t("milestones.threshold")}
            value={milestoneForm.threshold_value}
            onChange={(e) =>
              setMilestoneForm({
                ...milestoneForm,
                threshold_value: e.target.value,
              })
            }
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("schedules.channel")}</InputLabel>
            <Select
              value={milestoneForm.channel}
              label={t("schedules.channel")}
              onChange={(e) =>
                setMilestoneForm({ ...milestoneForm, channel: e.target.value })
              }
            >
              {CHANNELS.map((c) => (
                <MenuItem key={c} value={c}>
                  {t(`schedules.channels.${c}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            label={t("milestones.asset")}
            value={milestoneForm.asset}
            onChange={(e) =>
              setMilestoneForm({ ...milestoneForm, asset: e.target.value })
            }
            helperText={t("milestones.assetHint")}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography>{t("schedules.active")}</Typography>
            <Switch
              checked={milestoneForm.is_active}
              onChange={(e) =>
                setMilestoneForm({
                  ...milestoneForm,
                  is_active: e.target.checked,
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMilestoneDialog(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSaveMilestone}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
